import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { User, Run, Driver, ApplicationUuid } from '@bringg/types';
import { Table, Tooltip, BringgInput } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { observer } from 'mobx-react';

import { getUsersWithAvailableFloatingInventory } from '../../../../stores/floating-inventories';
import useStores from '../../../../recipes/use-stores';
import RunsList from '../../runs-list';
import getTeamId from '../../run-assigner-team-id-provider';
import { actions, SourceData, UserAssignmentData } from '../consts';
import useUserAssignerStore from '../use-user-assigner-store';

import './user-assigner.scss';

export interface UserAssignerProps {
	dateRange: { startTime; endTime };
	source: SourceData;
	data: UserAssignmentData;
}

const UserAssigner: React.FC<UserAssignerProps> = ({ dateRange, source, data }: UserAssignerProps) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');
	const [runsByDrivers, setRunsByDrivers] = useState<{ [driverId: number]: { runId: Run } }>(null);
	const [driversSkillsMatch, setDriversSkillsMatch] = useState<{ [driverId: number]: boolean }>({});
	const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
	const [selectedRunIds, setSelectedRunIds] = useState<number[]>([]);
	const [filteredDrivers, setFilteredDrivers] = useState<User[]>([]);
	const [sortedDrivers, setSortedDrivers] = useState<User[]>([]);
	const [expandedRow, setExpandedRow] = useState<number[]>([]);
	const [driversInventoryCompliant, setDriversInventoryCompliant] = useState<{ [driverId: number]: boolean }>({});
	const [showInventoryCompliantColumn, setShowInventoryCompliantColumn] = useState(false);
	const [loadingInventoryCompliant, setLoadingInventoryCompliant] = useState(true);
	const { runStore, merchantConfigurationsStore } = useStores();

	const { selectedDriver, selectedRun, selectDriver, selectRun, unstartedRunTasks, runsById } =
		useUserAssignerStore();

	const allowedActions = data.allowedActions as string[];

	const fetchDriversWithAvailableInventory = async (drivers: User[]) => {
		if (!merchantConfigurationsStore.findApplication(ApplicationUuid.FloatingInventoryApp)) return;

		const tasks = source.tasks || unstartedRunTasks;
		if (!tasks || tasks?.length !== 1) return;

		const usersWithAvailableFloatingInventory = await getUsersWithAvailableFloatingInventory(tasks[0].id);
		setShowInventoryCompliantColumn(true);
		if (!usersWithAvailableFloatingInventory?.length) {
			setLoadingInventoryCompliant(false);
			return;
		}

		const inventoryCompliantMatch = {};
		for (const driver of drivers) {
			inventoryCompliantMatch[driver.id] = usersWithAvailableFloatingInventory?.includes(driver.id);
		}
		setDriversInventoryCompliant(inventoryCompliantMatch);
		setLoadingInventoryCompliant(false);
	};

	const getMatchSkills = () => {
		const skillsMatch = {};
		let totalSkills = [];

		if (source.tasks) {
			totalSkills = source.tasks.map(task => task.required_skills).flat();
		} else {
			for (const task of unstartedRunTasks) {
				if (task.required_skills) {
					totalSkills = totalSkills.concat(task.required_skills);
				}
			}
		}

		for (const driver of data.drivers) {
			skillsMatch[driver.id] = totalSkills.every(runSkill => {
				return driver.skills.includes(runSkill);
			});
		}
		return skillsMatch;
	};

	useEffect(() => {
		const runsByDriversMap = {};

		fetchDriversWithAvailableInventory(data.drivers);

		for (const run of Object.values(runsById)) {
			if (!runsByDriversMap[run.user_id]) {
				runsByDriversMap[run.user_id] = {};
			}

			runsByDriversMap[run.user_id][run.id] = run;
		}

		const skillsMatch = getMatchSkills();

		if (source.run) {
			setExpandedRow([source.run.user_id]);
		}

		setRunsByDrivers(runsByDriversMap);
		setDriversSkillsMatch(skillsMatch);
		setFilteredDrivers(sortDrivers());
	}, []);

	useEffect(() => {
		if (selectedDriver && selectedDriver.id !== selectedDriverIds[0]) {
			setSelectedDriverIds([selectedDriver.id]);
		}
	}, [selectedDriver, selectedDriverIds]);

	useEffect(() => {
		if (selectedRun && selectedRun.id !== selectedRunIds[0]) {
			setSelectedRunIds([selectedRun.id]);
		}
	}, [selectedRun, selectedRunIds]);

	const shouldRenderDriverRankColumn = useMemo(() => {
		const driverWithRank = data.drivers.find(driver => driver.rank && driver.rank != 5);
		return !!driverWithRank;
	}, [data]);

	const sortDrivers = () => {
		const drivers = data.drivers.slice().sort((driverA, driverB) => {
			if (isDriverOfSourceRun(driverA.id)) {
				return -1;
			}

			if (isDriverOfSourceRun(driverB.id)) {
				return 1;
			}

			return driverA.name.toString().localeCompare(driverB.name.toString());
		});

		setSortedDrivers(drivers);

		return drivers;
	};

	const onDriverSelected = useCallback(
		driverIds => {
			const driverId = driverIds[0];
			const isAlreadySelected = driverId === selectedDriverIds[0];

			if (isExpandable(driverId)) {
				if (!expandedRow.length || expandedRow[0] !== driverId) {
					setExpandedRow([driverId]);
				} else {
					setExpandedRow([]);
				}
			}

			if (isDriverOfSourceRun(driverId) || isAlreadySelected) {
				return;
			}

			setSelectedDriverIds(driverIds);
			setSelectedRunIds([]);

			selectDriver(data.drivers.find(currentDriver => currentDriver.id === driverIds[0]));
		},
		[selectedDriverIds, runsByDrivers, data.drivers, selectDriver, expandedRow]
	);

	const onRunSelected = useCallback(
		runIds => {
			if (runIds[0] === selectedRunIds[0]) {
				return;
			}
			setSelectedRunIds(runIds);
			setSelectedDriverIds([]);
			selectRun(runsById[runIds[0]]);
		},
		[selectedRunIds]
	);

	const searchDrivers = useCallback(
		event => {
			const foundDrivers = event.target.value
				? sortedDrivers.filter(
						driver => driver.name.toLowerCase().indexOf(event.target.value.toLowerCase()) > -1
				  )
				: sortedDrivers;

			setFilteredDrivers(foundDrivers);
			setSearch(event.target.value);
		},
		[sortedDrivers]
	);

	const isDriverOfSourceRun = driverId => {
		return source.run && runsById[source.run.id].user_id === driverId;
	};

	function getDriverStatus(driver: Driver) {
		if (!driver.active_shift_id) {
			return {
				class: 'driver-shift-indication-not-active',
				tooltipText: 'RUN_USER_ASSIGNMENT.OFF_SHIFT'
			};
		}

		if (driver.current_unavailability_id) {
			return {
				class: 'driver-shift-indication-unavailable',
				tooltipText: 'RUN_USER_ASSIGNMENT.DRIVER_UNAVAILABLE'
			};
		}

		if (driver.is_on_break) {
			return {
				class: 'driver-shift-indication-on-break',
				tooltipText: 'RUN_USER_ASSIGNMENT.DRIVER_ON_BREAK'
			};
		}

		return {
			class: 'driver-shift-indication-active',
			tooltipText: 'RUN_USER_ASSIGNMENT.ON_SHIFT'
		};
	}

	const inventoryCompliantColumn = showInventoryCompliantColumn
		? {
				title: t('RUN_USER_ASSIGNMENT.INVENTORY_COMPLIANT'),
				key: 'isInventoryCompliantMatch',
				width: '200px',
				render: (driver: Driver) => {
					const loadingIconElement = <BringgIcon iconName={BringgFontIcons.Loader} spin={true} />;
					const iconElement = (
						<BringgIcon
							iconName={
								driversInventoryCompliant[driver.id] ? BringgFontIcons.Selected : BringgFontIcons.Close
							}
							className={
								driversInventoryCompliant[driver.id]
									? 'driver-icon-match-success'
									: 'driver-icon-match-fail'
							}
							data-test-id={`inventory-compliant-icon-${driver.id}`}
						/>
					);

					return (
						<div
							className="center-text inventory-compliant-match"
							data-test-id={`inventory-compliant-${driver.id}`}
						>
							{loadingInventoryCompliant ? loadingIconElement : iconElement}
						</div>
					);
				}
		  }
		: {};

	const driverRankColumn = () => {
		return shouldRenderDriverRankColumn
			? {
					title: t('RUN_USER_ASSIGNMENT.DRIVER_RANK'),
					key: 'rank',
					width: '100px',
					render: driver => (
						<div className="center-text" data-test-id={`rank-${driver.id}`}>
							{driver.rank}
						</div>
					)
			  }
			: {};
	};

	const columns = [
		{
			title: '',
			key: 'on-shift-indication',
			width: '30px',
			render: driver => {
				const driverStatus = getDriverStatus(driver);

				return (
					<Tooltip
						key="on-shift-indication-tooltip"
						title={t(driverStatus.tooltipText)}
						placement="top"
						overlayClassName="runs-tooltip"
					>
						<BringgIcon
							iconName={BringgFontIcons.Circle}
							className={classNames('driver-shift-indication', driverStatus.class)}
							data-test-id={`driver-shift-indication-${driver.id}`}
						/>
					</Tooltip>
				);
			}
		},
		{
			title: t('RUN_USER_ASSIGNMENT.DRIVER_NAME'),
			key: 'name',
			render: driver => (
				<span data-test-id={`driver-name-${driver.id}`}>
					<span className="driver-name">
						{driver.name} {isDriverOfSourceRun(driver.id)}
					</span>
					{isDriverOfSourceRun(driver.id) && (
						<span className="current-driver" data-test-id={`driver-name-${driver.id}`}>
							{t('RUN_USER_ASSIGNMENT.CURRENT_DRIVER')}
						</span>
					)}
				</span>
			)
		},
		driverRankColumn(),
		inventoryCompliantColumn,
		{
			title: t('RUN_USER_ASSIGNMENT.ROUTES_COUNT'),
			key: 'routesCount',
			width: '90px',
			render: driver => (
				<div className="routes-count center-text" data-test-id={`routes-count-${driver.id}`}>
					{Object.keys(runsByDrivers[driver.id] || {}).length}
				</div>
			)
		},
		{
			title: t('RUN_USER_ASSIGNMENT.MATCH_SKILLS'),
			key: 'doSkillsMatch',
			width: '100px',
			render: driver => (
				<div className="center-text skills-match" data-test-id={`skills-match-${driver.id}`}>
					<BringgIcon
						iconName={driversSkillsMatch[driver.id] ? BringgFontIcons.Selected : BringgFontIcons.Close}
						className={
							driversSkillsMatch[driver.id] ? 'driver-icon-match-success' : 'driver-icon-match-fail'
						}
						data-test-id={`skills-icon-${driver.id}`}
					/>
				</div>
			)
		}
	];

	const isExpandable = (driverId: number) => {
		return Object.keys(runsByDrivers[driverId] || {}).length > 0;
	};

	const renderExpandIcon = ({ expanded, expandable }: any) => {
		if (!expandable) return null;

		const icon = expanded ? BringgFontIcons.Chevron : BringgFontIcons.ChevronRight;

		return (
			<BringgIcon
				iconName={icon}
				className="route-reassignment-icon-wrapper route-reassignment-icon"
				data-test-id="route-reassignment-icon"
			/>
		);
	};

	const createRunFromSource = (): Run => {
		if (source.run) {
			return runStore.get(source.run.id) || source.run;
		}

		if (source.tasks?.length) {
			return {
				id: 0,
				scheduled_start_time: dateRange.startTime,
				scheduled_end_time: dateRange.endTime,
				team_id: getTeamId(source),
				task_ids: source.tasks.map(task => task.id)
			} as Run;
		}
	};

	return (
		<div className="user-assigner">
			<div className="user-assigner-description">
				<div>{t('RUN_USER_ASSIGNMENT.DRIVER_DESCRIPTION')}</div>
				{(!allowedActions || allowedActions.includes(actions.merge)) && (
					<div>{t('RUN_USER_ASSIGNMENT.RUN_DESCRIPTION')}</div>
				)}
			</div>

			<div className="selected-run-row">
				<span className="selected-run-text">
					{t(source.run ? 'RUN_USER_ASSIGNMENT.SELECTED_RUN' : 'RUN_USER_ASSIGNMENT.SELECTED_TASKS')}
				</span>
				<RunsList
					selectedRunTasks={unstartedRunTasks}
					runs={[createRunFromSource()]}
					sourceTasks={source.tasks}
					selectedRunIds={null}
					selectedTasksWeight={source.selectedTasksWeight}
				/>
			</div>

			<div className="search-row">
				<BringgInput
					className="search-row-input"
					placeholder={t('RUN_USER_ASSIGNMENT.SEARCH_PLACEHOLDER')}
					value={search}
					onChange={searchDrivers}
					suffix={<BringgIcon iconName={BringgFontIcons.Search} />}
				/>
			</div>
			<div className="users-table">
				<Table
					rowKey="id"
					className="route-reassignment-drivers"
					scroll={{ y: 350 }}
					columns={columns}
					tableLayout="auto"
					expandable={{
						columnWidth: 12,
						expandIconColumnIndex: 2,
						expandedRowKeys: expandedRow,
						expandedRowRender: driver => (
							<RunsList
								selectedRunTasks={unstartedRunTasks}
								runs={Object.values(runsByDrivers[driver.id])}
								sourceRun={source?.run}
								onRunSelected={onRunSelected}
								selectedRunIds={selectedRunIds}
								isSelectable={!allowedActions || allowedActions.includes(actions.merge)}
								selectedTasksWeight={source.selectedTasksWeight}
							/>
						),
						rowExpandable: driver => isExpandable(driver.id),
						expandIcon: renderExpandIcon
					}}
					rowSelection={{
						selectedRowKeys: selectedDriverIds,
						onChange: driverIds => onDriverSelected(driverIds),
						type: 'radio'
					}}
					dataSource={filteredDrivers}
					onRow={driver => ({
						onClick: () => {
							onDriverSelected([driver.id]);
						}
					})}
				/>
			</div>
		</div>
	);
};

export default observer(UserAssigner);
